/**
 * Settings · Systems (`/settings/systems`, YAG-1108): the workspace's declared
 * systems, one row each, with how many repositories it lists and the Teams
 * that pick it. The sheet holds the YAML; New system opens the form there.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Btn, Group, Groups, Row } from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { NewSystemSheet, SystemSheet, reposTag, teamsLine } from "./SystemSheet.js";
import {
  SYSTEM_CONFIG_UNREADABLE,
  listSystems,
  systemErrorOf,
  type DeclaredSystemSummary,
} from "./systemsApi.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; systems: DeclaredSystemSummary[] }
  | { kind: "error"; error: string };

const MARK_STYLE = { "--c": "var(--agent-qa)" } as CSSProperties;

export function SystemsSection() {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoad({ kind: "loaded", systems: await listSystems() });
    } catch (err) {
      setLoad({ kind: "error", error: systemErrorOf(err).message });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const systems = load.kind === "loaded" ? load.systems : [];
  const open = openId ? (systems.find((system) => system.id === openId) ?? null) : null;
  const sheet = creating ? (
    <NewSystemSheet
      onCreated={(system) => {
        setCreating(false);
        void reload().then(() => setOpenId(system.id));
      }}
      onCancel={() => setCreating(false)}
    />
  ) : open ? (
    <SystemSheet
      key={open.id}
      summary={open}
      onChanged={() => void reload()}
      onClose={() => setOpenId(null)}
    />
  ) : null;
  useOwnedSheet(sheet, () => {
    setCreating(false);
    setOpenId(null);
  });

  return (
    <Groups>
      {load.kind === "error" && (
        <div className="qnote err" role="alert">
          <span>{load.error}</span>
        </div>
      )}
      {load.kind === "loaded" && systems.length === 0 ? (
        <div className="empty dashed tight" data-testid="systems-empty">
          <p>
            No systems yet. A system names the repositories that run together, and a Team that picks
            one builds with all of them.
          </p>
        </div>
      ) : (
        <Group
          label="Systems"
          plain
          empty={load.kind === "loading" ? "Reading the systems." : undefined}
        >
          {systems.map((system) => (
            <Row
              key={system.id}
              glyph={
                <span className="cmark" style={MARK_STYLE} aria-hidden>
                  S
                </span>
              }
              rowKey="SYSTEM"
              title={system.name}
              tag={{ label: reposTag(system.repos), color: "var(--text-quiet)" }}
              state={system.configUnreadable ? SYSTEM_CONFIG_UNREADABLE : teamsLine(system.teams)}
              verb="Open"
              sel={!creating && system.id === openId}
              onOpen={() => {
                setCreating(false);
                setOpenId(system.id);
              }}
            />
          ))}
        </Group>
      )}
      <div className="qline">
        <span className="fact">A Team picks its system in its settings.</span>
        <span className="acts">
          <Btn
            variant="secondary"
            size="sm"
            data-testid="system-new"
            onClick={() => {
              setOpenId(null);
              setCreating(true);
            }}
          >
            New system
          </Btn>
        </span>
      </div>
    </Groups>
  );
}
